import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { PublicLayout } from "@/components/site/PublicLayout";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, fileUrl } from "@/lib/api";

const Icon = ({ name, size = 16, className = "" }) => {
    const C = Icons[name] || Icons.CheckCircle2;
    return <C size={size} className={className} />;
};

export default function SocietyDetail() {
    const { slug } = useParams();
    const [s, setS] = useState(null);
    const [committee, setCommittee] = useState([]);
    const [docs, setDocs] = useState([]);
    const [albums, setAlbums] = useState([]);
    const [events, setEvents] = useState([]);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setNotFound(false);
        api.get(`/societies/${slug}`)
            .then((r) => {
                setS(r.data);
                api.get(`/societies/${r.data.id}/committee`).then((rr) => setCommittee(rr.data));
                api.get("/documents", { params: { society_id: r.data.id, limit: 50 } }).then((rr) => setDocs(rr.data));
                api.get("/gallery/albums", { params: { society_id: r.data.id } }).then((rr) => setAlbums(rr.data));
                api.get("/events", { params: { society_id: r.data.id } }).then((rr) => setEvents(rr.data));
            })
            .catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) return <PublicLayout><div className="container-x py-24 text-center text-slate-500">Society not found.</div></PublicLayout>;
    if (!s) return <PublicLayout><div className="container-x py-24 text-center text-slate-400">Loading…</div></PublicLayout>;

    return (
        <PublicLayout>
            {/* HEADER */}
            <section className="relative overflow-hidden bg-brand-blue text-white">
                <div className="absolute inset-0 opacity-25">
                    {s.cover_id ? (
                        <img src={fileUrl(s.cover_id)} alt="" className="h-full w-full object-cover" />
                    ) : (
                        <div className="h-full w-full bg-gradient-to-br from-brand-blue via-brand-blue-dark to-slate-900" />
                    )}
                </div>
                <div className="container-x relative py-16 md:py-20">
                    <Breadcrumbs items={[{ label: "Societies", to: "/societies" }, { label: s.name }]} />
                    <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
                        <div className="max-w-3xl">
                            <div className="flex items-center gap-3">
                                {s.logo_id && <img src={fileUrl(s.logo_id)} alt="" className="h-12 w-12 rounded-full bg-white p-1" />}
                                <span className="rounded-full bg-white/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em]">Member Society</span>
                            </div>
                            <h1 className="mt-4 font-serif text-4xl leading-tight sm:text-5xl">{s.name}</h1>
                            <p className="mt-3 text-white/80">{s.short_description}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4 rounded-2xl bg-white/10 p-5 backdrop-blur-sm sm:grid-cols-4">
                            <Stat label="Buildings" value={s.buildings} />
                            <Stat label="Wings" value={s.wings} />
                            <Stat label="Flats" value={s.flats} />
                            <Stat label="Members" value={s.members} />
                        </div>
                    </div>
                </div>
            </section>

            <section className="container-x py-12">
                <Tabs defaultValue="overview" className="w-full">
                    <TabsList className="flex flex-wrap justify-start gap-2 bg-transparent p-0" data-testid="society-tabs">
                        {[
                            ["overview", "Overview"],
                            ["committee", "Committee"],
                            ["documents", "Documents"],
                            ["gallery", "Gallery"],
                            ["events", "Events"],
                            ["contact", "Contact"],
                        ].map(([v, l]) => (
                            <TabsTrigger
                                key={v}
                                value={v}
                                className="rounded-full border border-slate-200 bg-white px-5 py-2 text-sm data-[state=active]:border-brand-blue data-[state=active]:bg-brand-blue data-[state=active]:text-white"
                                data-testid={`society-tab-${v}`}
                            >{l}</TabsTrigger>
                        ))}
                    </TabsList>

                    <TabsContent value="overview" className="mt-8">
                        <div className="grid gap-10 md:grid-cols-12">
                            <div className="md:col-span-8">
                                <div className="overline">About</div>
                                <h2 className="mt-3 font-serif text-3xl text-slate-900">About {s.name}</h2>
                                <p className="mt-4 leading-relaxed text-slate-600">{s.about}</p>

                                {s.facilities?.length > 0 && (
                                    <div className="mt-10">
                                        <div className="overline">Facilities</div>
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            {s.facilities.map((f) => (
                                                <span key={f.id} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm text-slate-700">
                                                    <Icon name={f.icon} className="text-brand-blue" /> {f.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {s.amenities?.length > 0 && (
                                    <div className="mt-10">
                                        <div className="overline">Amenities</div>
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            {s.amenities.map((f) => (
                                                <span key={f.id} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-brand-orange-light px-4 py-1.5 text-sm text-slate-700">
                                                    <Icon name={f.icon} className="text-brand-orange" /> {f.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <aside className="md:col-span-4">
                                <div className="surface-card p-6">
                                    <div className="overline">Registration</div>
                                    <dl className="mt-4 space-y-3 text-sm">
                                        <Field label="Reg No." value={s.registration_number} />
                                        <Field label="Reg Date" value={s.registration_date} />
                                        <Field label="Address" value={s.address} />
                                    </dl>
                                    {s.registration_certificate_id && (
                                        <a href={fileUrl(s.registration_certificate_id)} target="_blank" rel="noreferrer" className="btn-outline mt-5 w-full">
                                            View Certificate <Icons.ExternalLink size={14} />
                                        </a>
                                    )}
                                </div>
                            </aside>
                        </div>
                    </TabsContent>

                    <TabsContent value="committee" className="mt-8">
                        {committee.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-slate-200 py-16 text-center text-slate-500">No committee members added yet.</div>
                        ) : (
                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {committee.map((m) => (
                                    <div key={m.id} className="surface-card p-6">
                                        <div className="flex items-center gap-4">
                                            {m.photo_id ? (
                                                <img src={fileUrl(m.photo_id)} alt={m.name} className="h-14 w-14 rounded-full object-cover" />
                                            ) : (
                                                <div className="grid h-14 w-14 place-items-center rounded-full bg-brand-blue-light font-serif text-xl text-brand-blue">{m.name.charAt(0)}</div>
                                            )}
                                            <div>
                                                <div className="font-serif text-lg text-slate-900">{m.name}</div>
                                                <div className="text-xs text-slate-500">{m.designation}</div>
                                            </div>
                                        </div>
                                        <div className="mt-4 space-y-1 text-sm text-slate-600">
                                            {m.mobile && <div className="flex items-center gap-2"><Icons.Phone size={14} className="text-brand-orange" /> {m.mobile}</div>}
                                            {m.email && <div className="flex items-center gap-2"><Icons.Mail size={14} className="text-brand-orange" /> {m.email}</div>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="documents" className="mt-8">
                        {docs.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-slate-200 py-16 text-center text-slate-500">No documents uploaded yet.</div>
                        ) : (
                            <div className="grid gap-3">
                                {docs.map((d) => (
                                    <div key={d.id} className="surface-card flex items-center gap-4 p-5">
                                        <div className="grid h-11 w-11 place-items-center rounded-lg bg-brand-blue-light text-brand-blue"><Icons.FileText size={18} /></div>
                                        <div className="min-w-0 flex-1">
                                            <div className="font-medium text-slate-900">{d.title}</div>
                                            <div className="text-xs text-slate-500">{d.category}</div>
                                        </div>
                                        {d.file_id && (
                                            <a href={fileUrl(d.file_id)} target="_blank" rel="noreferrer" className="btn-outline !py-2">Download</a>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="gallery" className="mt-8">
                        {albums.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-slate-200 py-16 text-center text-slate-500">No albums yet.</div>
                        ) : (
                            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                                {albums.map((a) => (
                                    <div key={a.id} className="surface-card overflow-hidden">
                                        <div className="h-40 bg-brand-blue-light">
                                            {a.cover_id && <img src={fileUrl(a.cover_id)} alt={a.title} className="h-full w-full object-cover" />}
                                        </div>
                                        <div className="p-5">
                                            <div className="font-serif text-lg">{a.title}</div>
                                            <div className="text-xs text-slate-500">{a.category}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="events" className="mt-8">
                        {events.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-slate-200 py-16 text-center text-slate-500">No events scheduled for this society yet.</div>
                        ) : (
                            <div className="grid gap-4 md:grid-cols-2">
                                {events.map((e) => (
                                    <article key={e.id} className="surface-card-hover overflow-hidden" data-testid={`society-event-${e.id}`}>
                                        <div className="relative h-40 bg-gradient-to-br from-brand-blue via-brand-blue-dark to-slate-900">
                                            {e.banner_id ? (
                                                <img src={fileUrl(e.banner_id)} alt={e.title} className="h-full w-full object-cover" loading="lazy" />
                                            ) : (
                                                <div className="grid h-full place-items-center text-white/40"><Icons.Calendar size={40} /></div>
                                            )}
                                            {e.date && (
                                                <div className="absolute right-3 top-3 rounded-xl bg-white p-2 text-center shadow-md">
                                                    <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-orange">{new Date(e.date).toLocaleString("en", { month: "short" })}</div>
                                                    <div className="font-serif text-xl text-slate-900">{new Date(e.date).getDate()}</div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-5">
                                            <div className="font-serif text-xl text-slate-900">{e.title}</div>
                                            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                                                {e.venue && <span className="inline-flex items-center gap-1"><Icons.MapPin size={11} /> {e.venue}</span>}
                                                {e.organizer && <span className="inline-flex items-center gap-1"><Icons.User size={11} /> {e.organizer}</span>}
                                            </div>
                                            <p className="mt-2 line-clamp-2 text-sm text-slate-600">{e.description}</p>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        )}
                    </TabsContent>


                    <TabsContent value="contact" className="mt-8">
                        <div className="grid gap-10 md:grid-cols-12">
                            <div className="md:col-span-5">
                                <ul className="space-y-4 text-slate-600">
                                    <li className="flex gap-3"><Icons.MapPin className="mt-1 text-brand-orange" size={18} /><span>{s.contact?.address || s.address}</span></li>
                                    {s.contact?.phone && <li className="flex gap-3"><Icons.Phone className="mt-1 text-brand-orange" size={18} />{s.contact.phone}</li>}
                                    {s.contact?.telephone && <li className="flex gap-3"><Icons.PhoneCall className="mt-1 text-brand-orange" size={18} />{s.contact.telephone}</li>}
                                    {s.contact?.email && <li className="flex gap-3"><Icons.Mail className="mt-1 text-brand-orange" size={18} />{s.contact.email}</li>}
                                    {s.contact?.office_timing && <li className="flex gap-3"><Icons.Clock className="mt-1 text-brand-orange" size={18} />{s.contact.office_timing}</li>}
                                </ul>
                            </div>
                            <div className="overflow-hidden rounded-2xl border border-slate-200 md:col-span-7">
                                {(s.map_embed_url || s.contact?.map_embed_url) ? (
                                    <iframe title="Map" src={s.map_embed_url || s.contact?.map_embed_url} className="h-80 w-full" loading="lazy" />
                                ) : (
                                    <div className="grid h-80 place-items-center text-slate-400">Map not available</div>
                                )}
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>
            </section>
        </PublicLayout>
    );
}

const Stat = ({ label, value }) => (
    <div>
        <div className="text-3xl font-semibold">{value ?? 0}</div>
        <div className="text-[10px] uppercase tracking-wider text-white/70">{label}</div>
    </div>
);

const Field = ({ label, value }) => (
    <div>
        <dt className="text-slate-500">{label}</dt>
        <dd className="font-medium text-slate-900">{value || "—"}</dd>
    </div>
);
